import React, { useEffect, useState } from "react";
import { Target, Edit3, Loader2, TrendingUp, TrendingDown, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { toast } from "sonner";

export default function GoalTrackerCard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/goals/current");
      setData(r.data);
      setTarget(String(r.data.target || ""));
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const n = parseFloat(target);
    if (Number.isNaN(n) || n <= 0) { toast.error("Inserisci un target valido"); return; }
    await api.post("/goals/set", { monthlyTarget: n });
    toast.success("Target aggiornato");
    setEditing(false);
    load();
  };

  if (!data) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-5 flex items-center justify-center h-32">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const pct = Math.min(100, data.progressPct || 0);
  const onPace = data.isOnPace;
  const remainingPerDay = data.needPerDay || 0;
  const realizedColor = onPace ? "text-emerald-700" : "text-amber-700";

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-[0_1px_3px_rgba(10,25,47,0.05)]" data-testid="goal-card">
      <div className="px-5 py-3.5 border-b border-[#F0F2F5] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[#B7955B]" />
          <h3 className="font-serif text-base text-slate-900 font-medium">Obiettivo del mese</h3>
        </div>
        <button onClick={() => setEditing(!editing)} data-testid="goal-edit" className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm">
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-5">
        {editing ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Target €:</span>
            <input
              type="number"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
              data-testid="goal-input"
              className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm tabular-nums focus:border-[#0A192F] focus:ring-2 focus:ring-slate-900/8"
              autoFocus
            />
            <button onClick={save} data-testid="goal-save" className="text-white text-sm px-3 py-2 rounded-sm" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >Salva</button>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between mb-2">
              <div className={`font-serif text-[2rem] font-light tabular-nums ${realizedColor}`}>{fmtEUR(data.realized || 0)}</div>
              <div className="text-xs text-slate-500 tabular-nums">/ {fmtEUR(data.target)}</div>
            </div>
            {/* progress bar */}
            <div className="relative h-3 bg-slate-100 rounded-sm overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-700 ${onPace ? "bg-emerald-500" : "bg-amber-500"}`}
                style={{ width: `${pct}%` }}
              />
              {/* expected pace marker */}
              <div
                className="absolute inset-y-0 w-0.5 bg-slate-700"
                style={{ left: `${Math.min(100, (data.expectedPace / data.target) * 100)}%` }}
                title="Ritmo previsto"
              />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4 text-center">
              <Stat icon={onPace ? TrendingUp : TrendingDown} tone={onPace ? "ok" : "warn"} label="Stato" value={`${pct.toFixed(0)}%`} sub={onPace ? "in linea" : "indietro"} />
              <Stat icon={Calendar} tone="neutral" label="Mancano" value={`${data.daysLeft}`} sub="giorni" />
              <Stat icon={Target} tone="gold" label="Servono/giorno" value={fmtEUR(remainingPerDay)} sub="per centrare" />
            </div>
            {data.pancia > 0 && (
              <div className="mt-3 pt-3 border-t border-[#F0F2F5] text-[11px] text-slate-500 leading-relaxed">
                In pancia {fmtEUR(data.pancia)} di provvigioni potenziali. Se chiudi tutto, raggiungi {fmtEUR(data.realized + data.pancia)}.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, tone, label, value, sub }) {
  const colors = {
    ok:      "text-emerald-700 bg-emerald-50",
    warn:    "text-amber-700 bg-amber-50",
    gold:    "text-[#8a6e3d] bg-[#F5EFE3]",
    neutral: "text-slate-700 bg-slate-50",
  }[tone] || "text-slate-700 bg-slate-50";
  return (
    <div className="flex flex-col items-center">
      <div className={`w-7 h-7 rounded-sm flex items-center justify-center mb-1 ${colors}`}><Icon className="w-3.5 h-3.5" /></div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900 tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-500">{sub}</div>
    </div>
  );
}
